/**
 *
 * TreatmentFlow
 *
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import * as PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import messages from "./messages";
import { makeStyles } from "@material-ui/core/styles";
import SimpleSearch from "basicComponents/SimpleSearch/SimpleSearch";
import Match from "images/MatchIcon";
import NoMatch from "images/NoMatchIcon";
import { Grid, IconButton } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(2),
  },
  title: {
    color: "black",
    fontWeight: "bold",
    fontSize: "large",
    flex: 1,
  },
  option: {
    width: "100%",
  },
  center: {
    textAlign: "center",
  },
  optionContainer: {
    padding: theme.spacing(1, 0),
  },
}));

const JAWS = {
  UPPER: "UPPER",
  BOTTOM: "BOTTOM",
};

export function TreatmentHeader({
  productTypes = [],
  disabled = false,
  onChange,
  upperProductTypeId,
  lowerProductTypeId,
}) {
  const classes = useStyles();

  const [upperProduct, setUpperProduct] = useState({
    index: productTypes.findIndex(({ id }) => id === upperProductTypeId),
    search: "",
    selectedId: upperProductTypeId,
  });
  const [lowerProduct, setLowerProduct] = useState({
    index: productTypes.findIndex(({ id }) => id === lowerProductTypeId),
    search: "",
    selectedId: lowerProductTypeId,
  });

  useEffect(
    () => {
      if (upperProduct.selectedId > 0 && lowerProduct.selectedId > 0) {
        onChange(lowerProduct.selectedId, upperProduct.selectedId);
      }
    },
    [upperProduct.selectedId, lowerProduct.selectedId, onChange],
  );


  let [clickNextAttempted,setClickNextAttempted] = useState(false)

  useEffect(() => {
    let mycb = (e) => {
      setClickNextAttempted(window.clickNextAttempted)
    }
    window.addEventListener('clickNextAttempted',mycb)

    return () => {
      window.removeEventListener('clickNextAttempted',mycb)
    }
  },[])

  const handleSearchValueChanged = useCallback(
    type => search => {
      if (type === JAWS.UPPER) {
        setUpperProduct({
          ...upperProduct,
          search,
        });
      } else {
        setLowerProduct({
          ...lowerProduct,
          search,
        });
      }
    },
    [lowerProduct, setUpperProduct, setLowerProduct, upperProduct],
  );

  const productItems = useCallback(
    type => {
      if (type === JAWS.UPPER) {
        return productTypes.filter(({ description, synonyms }) =>
          `${description.toLowerCase()} ${synonyms
            .map(({ synonym }) => synonym.toLowerCase())
            .join(" ")}`.includes(upperProduct.search.toLowerCase()),
        );
      }
      return productTypes.filter(({ description, synonyms }) =>
        `${description.toLowerCase()} ${synonyms
          .map(({ synonym }) => synonym.toLowerCase())
          .join(" ")}`.includes(lowerProduct.search.toLowerCase()),
      );
    },
    [productTypes, upperProduct.search, lowerProduct.search],
  );

  const handleProductChanged = useCallback(
    type => (_, index) => {
      const product = productItems(type)[index];
      const actualIndex = productTypes.findIndex(({ id }) => id === product.id);

      if (type === JAWS.UPPER) {
        setUpperProduct({
          ...upperProduct,
          index: actualIndex,
          selectedId: product.id,
          search: "",
        });
      } else {
        setLowerProduct({
          ...lowerProduct,
          index: actualIndex,
          selectedId: product.id,
          search: "",
        });
      }
    },
    [productItems, productTypes, upperProduct, lowerProduct],
  );

  const isMatch = useMemo(
    () =>
      upperProduct.index === -1 ||
      lowerProduct.index === -1 ||
      upperProduct.selectedId === lowerProduct.selectedId,
    [
      upperProduct.selectedId,
      upperProduct.index,
      lowerProduct.selectedId,
      lowerProduct.index,
    ],
  );

  const canMatch = useMemo(
    () => upperProduct.index > -1 || lowerProduct.index > -1,
    [upperProduct.index, lowerProduct.index],
  );

  const matchDisabled = useMemo(
    () => upperProduct.index > -1 && lowerProduct.index > -1,
    [upperProduct.index, lowerProduct.index],
  );

  const handleSetEqual = useCallback(
    () => {
      if (upperProduct.selectedId > 0) {
        setLowerProduct(upperProduct);
      } else {
        setUpperProduct(lowerProduct);
      }
    },
    [lowerProduct, upperProduct],
  );

  return (
    <Grid alignItems="center" container className={classes.container}>
      <Grid item md={6} xs={12}>
        <span className={classes.title}>
          <FormattedMessage {...messages.title} />
        </span>
      </Grid>
      <Grid
        alignItems="center"
        container
        item
        md={6}
        xs={12}
        spacing={1}
        className={classes.optionContainer}
      >
        <Grid item xs={12} sm={canMatch ? 5 : 6}>
          <SimpleSearch
            id="upperJaw"
            closable
            className={classes.option}
            placeholder={messages.upperJaw}
            value={upperProduct.search}
            onClick={handleProductChanged(JAWS.UPPER)}
            onValueChanged={handleSearchValueChanged(JAWS.UPPER)}
            selectedIndex={upperProduct.index}
            disabled={disabled}
            items={productItems(JAWS.UPPER).map(
              ({ description }) => description,
            )}
            error={upperProduct.index == -1 && window.clickNextAttempted}
          />
        </Grid>
        {canMatch && (
          <Grid className={classes.center} item xs={12} sm={2}>
            <IconButton onClick={handleSetEqual} disabled={matchDisabled}>
              {isMatch ? (
                <Match style={{ left: 0 }} />
              ) : (
                <NoMatch style={{ left: 0 }} />
              )}
            </IconButton>
          </Grid>
        )}
        <Grid item xs={12} sm={canMatch ? 5 : 6}>
          <SimpleSearch
            id="lowerJaw"
            closable
            className={classes.option}
            placeholder={messages.bottomJaw}
            value={lowerProduct.search}
            onClick={handleProductChanged(JAWS.BOTTOM)}
            onValueChanged={handleSearchValueChanged(JAWS.BOTTOM)}
            selectedIndex={lowerProduct.index}
            disabled={disabled}
            items={productItems(JAWS.BOTTOM).map(
              ({ description }) => description,
            )}
            error={lowerProduct.index == -1 && window.clickNextAttempted}
          />
        </Grid>
      </Grid>
    </Grid>
  );
}

TreatmentHeader.propTypes = {
  onChange: PropTypes.func.isRequired,
  productTypes: PropTypes.array,
  disabled: PropTypes.bool,
  upperProductTypeId: PropTypes.number,
  lowerProductTypeId: PropTypes.number,
};

export default TreatmentHeader;
